import { tag } from '../pageTour';
import type { TourLocation, TourStep } from '../tourDefinitions';

/*
 * The Alert Board tour, shared by the per-site board (/alert-board…) and the all-sites one
 * (/all-sites/alert-board…) — the same board, so the same explanation.
 *
 * The board has three layouts on three URLs, and "?" must explain the one on screen, so most of
 * the middle steps carry a `when` for their layout. Everything else drops out on its own when its
 * control isn't there: the live-stream section only exists while an alert is streaming, the edit
 * buttons only for people allowed to edit maps, Jump to group only with six or more groups.
 *
 * Written for someone who has never used the product: what each thing is FOR, in plain words.
 */

const isGis = ({ pathname }: TourLocation) => pathname.endsWith('/gis');
const isMap = ({ pathname }: TourLocation) => pathname.endsWith('/map');
const isHoneycomb = (at: TourLocation) => !isGis(at) && !isMap(at);

export function alertBoardSteps(allSites: boolean): TourStep[] {
  const scope = allSites ? 'all your sites' : 'this site';
  return [
    {
      target: tag('alert-board-status'),
      title: 'How many alerts need attention',
      description:
        `This tells you, at a glance, how busy things are. "Open" is the number of alerts on ${scope} that nobody has dealt with yet. "Live" is how many of those are streaming live video in the panel on the right. If it says "No open alerts", everything is calm.`,
      side: 'bottom',
    },

    // ── Which layout, and what this tour is about to explain ──
    {
      target: tag('alert-board-views'),
      title: 'Three ways to see the same cameras',
      description:
        'Honeycomb shows every camera as a tile, grouped together. GIS puts cameras on a drawing of the building (a floor plan). Map puts them on a real street map. You are on Honeycomb now, so this tour explains Honeycomb. Switch layouts any time, then press ? again to learn that one.',
      side: 'bottom',
      when: isHoneycomb,
    },
    {
      target: tag('alert-board-views'),
      title: 'Three ways to see the same cameras',
      description:
        'Honeycomb shows every camera as a tile, grouped together. GIS puts cameras on a drawing of the building (a floor plan). Map puts them on a real street map. You are on GIS now, so this tour explains the floor plan. Switch layouts any time, then press ? again to learn that one.',
      side: 'bottom',
      when: isGis,
    },
    {
      target: tag('alert-board-views'),
      title: 'Three ways to see the same cameras',
      description:
        'Honeycomb shows every camera as a tile, grouped together. GIS puts cameras on a drawing of the building (a floor plan). Map puts them on a real street map. You are on Map now, so this tour explains the map. Switch layouts any time, then press ? again to learn that one.',
      side: 'bottom',
      when: isMap,
    },
    {
      target: tag('alert-board-default'),
      title: 'Open on this layout every time',
      description:
        'Click "Set default" and the Alert Board will always open on the layout you are looking at now. It is saved on this computer only. When the current layout is already your default, the button just says "Default".',
      side: 'bottom',
    },

    // ── What the board is showing ──
    {
      target: tag('alert-board-chip'),
      title: 'What you are looking at',
      description: allSites
        ? 'This label says how the board is arranged. In Honeycomb, cameras are grouped by site, and you can see how many sites, cameras and groups there are.'
        : 'This label says how the board is arranged. In Honeycomb, cameras are grouped by camera group (for example "Entrance" or "Parking"), and you can see how many cameras and groups there are.',
      side: 'bottom',
      when: isHoneycomb,
    },
    {
      target: tag('alert-board-chip'),
      title: 'Which floor plan this is',
      description:
        'The name of the building and floor you are looking at, and how many cameras are pinned on it. Click a pin once to see its live video. Double-click a pin to see where the camera points and how it is mounted.',
      side: 'bottom',
      when: isGis,
    },
    {
      target: tag('alert-board-chip'),
      title: 'How many cameras are on the map',
      description:
        'How many cameras have been placed on the street map. Click a pin to see its live video. Where pins sit close together they join into one round group: click a group to zoom in and split it.',
      side: 'bottom',
      when: isMap,
    },
    {
      target: tag('alert-board-filters'),
      title: 'Filters — show only what matters',
      description:
        'Click here to narrow the board down. The number shows how many cameras match right now. Cameras that do not match are faded out, so the important ones stand out. The same filters also apply to the alert list on the right.',
      side: 'bottom',
    },
    {
      target: tag('alert-board-filter-panel'),
      title: 'Filters — show only what matters',
      description: `${allSites ? 'Site keeps one site on the board. ' : ''}Alert type shows only one kind of alert, such as fire or intrusion. Severity picks Critical (most serious), Warning or Info; pick none to see all. "Only cameras with open alerts" hides the calm ones. Cameras that don't match are faded out. Clear puts everything back, and the arrow at the top folds this panel away.`,
      side: 'right',
    },

    // ── Honeycomb ──
    {
      target: tag('alert-board-tools'),
      title: 'Zoom and full screen',
      description:
        '− and + zoom out and in; the percentage is the current size. Fit goes back to the normal size. All shrinks every group to fit on one screen, so you can see if anything is red anywhere. The last button fills the whole screen, which is good for a control-room display; press Esc to leave. You can also scroll to move around, and hold Ctrl (⌘ on Mac) while scrolling to zoom.',
      side: 'bottom',
      when: isHoneycomb,
    },
    {
      target: tag('alert-board-jump'),
      title: 'Jump to a group',
      description: 'With many groups, pick one from this list and the board scrolls straight to it.',
      side: 'bottom',
      when: isHoneycomb,
    },
    {
      target: tag('alert-board-group'),
      title: allSites ? 'A site and its group' : 'A camera group',
      description:
        'Each group of tiles has a heading with its name and how many cameras it holds. The badge tells you its state: "All clear" means no alerts, a red number means that many open alerts, and "offline" means cameras that are not working.',
      side: 'bottom',
      when: isHoneycomb,
    },
    {
      target: tag('alert-board-hex'),
      title: 'A camera tile',
      description:
        'Each six-sided tile is one camera. Grey means working and calm. Dark means offline. Red, orange or blue means it has an alert (Critical, Warning or Info), and the tile pulses: the faster it pulses, the more serious the alert. The white number is how many alerts it has. Hover over a tile to see its latest alert; click it to watch the camera live.',
      side: 'right',
      when: isHoneycomb,
    },

    // ── GIS (floor plan) ──
    {
      target: tag('alert-board-layouts'),
      title: 'Choose a floor plan',
      description:
        'The stack button lists every saved floor plan: click one to switch building or floor. A red number on it counts alerts on the OTHER plans, so you know when something is happening on a floor you are not looking at. The star marks the default plan. People allowed to edit maps also see + to add a new plan, and buttons to edit, set as default or delete a plan.',
      side: 'bottom',
      when: isGis,
    },
    {
      target: tag('alert-board-tools'),
      title: 'Zoom and full screen',
      description:
        '− and + zoom out and in. Fit shows the whole floor plan again. The last button fills the whole screen; press Esc to leave. You can also drag the plan to move around.',
      side: 'bottom',
      when: isGis,
    },
    {
      target: tag('map-pin'),
      title: 'A camera on the plan',
      description:
        'Each pin is a camera, and the shaded cone shows the direction it is looking. Dark blue means working, pale means offline, and red, orange or blue with ripples means an alert. The two small dots under it show if live video (left) and recording (right) are running: green yes, red no. Click once to watch it live; double-click to see its mounting details.',
      side: 'right',
      when: isGis,
    },

    // ── Map (street map) ──
    {
      target: tag('alert-board-map-edit'),
      title: 'Place cameras and save the view',
      description:
        'The pin button lets you put cameras on the map: pick a camera, click where it is, and drag to adjust. Then press ✓ to save or ✕ to cancel (the alert list hides while you edit). "Set default view" saves the current position and zoom of the map, so everyone on this site opens the map right here.',
      side: 'bottom',
      when: isMap,
    },
    {
      target: tag('map-place-search'),
      title: 'Find a place',
      description:
        'Type a place name, or paste map coordinates like 19.076, 72.878, then press Go. The map moves there.',
      side: 'bottom',
      when: isMap,
    },
    {
      target: tag('alert-board-tools'),
      title: 'Zoom and full screen',
      description:
        '− and + zoom out and in. Fit moves the map to show every camera. Default goes back to the saved starting view. The last button fills the whole screen; press Esc to leave. You can also drag the map and scroll to zoom.',
      side: 'bottom',
      when: isMap,
    },
    {
      target: tag('map-cluster'),
      title: 'A group of cameras',
      description:
        'When cameras are too close together to show separately, they join into one circle with the number of cameras inside. A red badge shows their alerts. Click the circle to zoom in and split it into single cameras.',
      side: 'right',
      when: isMap,
    },
    {
      target: tag('map-pin'),
      title: 'A camera on the map',
      description:
        'Each pin is a camera, and the shaded cone shows the direction it is looking. Dark blue means working, pale means offline, and red, orange or blue with ripples means an alert. The two small dots show if live video (left) and recording (right) are running. Click a pin to watch the camera live.',
      side: 'right',
      when: isMap,
    },
    {
      target: tag('map-basemap'),
      title: 'Map style',
      description:
        'Change how the map looks: Map is the normal map, Streets is a street-style map, and Satellite and Terrain show photos and hills. Satellite and Terrain need an internet connection.',
      side: 'top',
      when: isMap,
    },

    // ── Every layout ──
    {
      target: tag('alert-board-legend'),
      title: 'What the colours mean',
      description:
        'A quick reminder. The two dots on every camera: left is live video, right is recording, and green means running, red means stopped. The colour of a pulsing camera shows how serious its alert is: red is Critical, orange is Warning, blue is Info.',
      side: 'top',
    },
    {
      target: tag('alert-board-alerts'),
      title: 'Active alerts',
      description:
        'Every open alert for the cameras on this board, newest first. It follows the board\'s filters. When nothing is happening it says "No active alerts".',
      side: 'left',
    },
    {
      target: tag('alert-board-live'),
      title: 'Live video of new alerts',
      description:
        'When a new alert comes in while this page is open, its camera starts playing live here. A Critical alert keeps playing until someone deals with it ("live until actioned"). Older videos close after a minute and show a countdown; press "Continue live stream" to keep one open longer. The badge shows how many alerts that camera has. Click the video to find the camera on the board.',
      side: 'left',
    },
    {
      target: tag('alert-board-preview'),
      title: 'Alert preview',
      description:
        'A list of every open alert with a picture of the moment it happened. Scroll down and more load automatically.',
      side: 'left',
    },
    {
      target: tag('alert-board-card'),
      title: 'One alert',
      description:
        'Shows the alert\'s name, the camera, how long ago it happened and what was seen. Click the card to open the full alert with its picture and video; use the arrow keys there to go through the alerts one by one.',
      side: 'left',
    },
    {
      target: tag('alert-board-ack'),
      title: 'Acknowledge',
      description:
        'Ack means "I have seen this and someone is on it." The alert leaves the open list, the counters go down and the camera stops pulsing. To close an alert completely (resolve it), open the alert and do it there.',
      side: 'left',
    },
  ];
}
